import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  Type,
} from '@angular/core';
import { Subject, Subscription, debounceTime } from 'rxjs';

import {
  DashboardContext,
  DefaultDashboardDefinition,
  resolveDashboardId,
  resolveDashboardPlacements,
} from '../../default-dasboards/dashboard-definition';
import { DashboardLayout } from '../../dashboard-layout';
import {
  DashboardOwnerScope,
  DashboardWidgetDefinition,
  DashboardWidgetPlacement,
  normalizeDashboardPlacements,
} from '../../widget-and-graphs.models';
import {
  WIDGET_DEFINITIONS,
  defaultDashboardPlacements,
} from '../../widgets';
import { DashboardGraphWidget } from '../dashboard-graph-widget/dashboard-graph-widget';
import { DashboardSankeyWidget } from '../dashboard-sankey-widget/dashboard-sankey-widget';
import { DashboardListWidget } from '../dashboard-list-widget/dashboard-list-widget';
import { DashboardPositionWidget } from '../dashboard-position-widget/dashboard-position-widget';
import { DashboardFinanceMoneyWidget } from '../dashboard-finance-money-widget/dashboard-finance-money-widget';
import { DashboardNumberWidget } from '../dashboard-number-widget/dashboard-number-widget';
import { DashboardRecordMetric } from '../dashboard-record-metric/dashboard-record-metric';
import { DashboardPercentageWidget } from '../dashboard-percentage-widget/dashboard-percentage-widget';
import { DashboardWelcomeWidget } from '../dashboard-welcome-widget/dashboard-welcome-widget';
import { ReportWidget } from '../report-widget/report-widget';

export interface DashboardGridSize {
  width: number;
  height: number;
}

export interface DashboardWidgetLayout {
  xs: DashboardGridSize;
  sm?: DashboardGridSize;
  md?: DashboardGridSize;
  lg?: DashboardGridSize;
  xl?: DashboardGridSize;
}

interface EmptyDashboardCell {
  x: number;
  y: number;
}

@Component({
  selector: 'app-dashboard-grid',
  standalone: false,
  templateUrl: './dashboard-grid.html',
  styleUrl: './dashboard-grid.scss',
})
export class DashboardGrid implements OnInit, OnChanges, OnDestroy {
  @Input() dashboardId = '';
  @Input() defaultDashboard?: DefaultDashboardDefinition;
  @Input() dashboardContext: DashboardContext = {};
  @Input() appId = 'home';
  @Input() ownerScope: DashboardOwnerScope = 'user';
  @Input() editable = true;
  @Input() definitions: readonly DashboardWidgetDefinition[] = WIDGET_DEFINITIONS;
  @Input() defaultWidgets?: DashboardWidgetPlacement[];
  @Input() lockedInputs: Record<string, unknown> = {};
  /** Owner-controlled inputs applied after persisted customizations, per widget. */
  @Input() lockedWidgetInputs: Readonly<Record<string, Record<string, unknown>>> = {};
  /** Record dashboards reapply their canonical source and predicate after saved overrides. */
  @Input() queryScope: { source: string; where: Record<string, unknown> } | null = null;
  @Input() columns = 12;
  @Input() gap = 16;

  @Output() widgetsChange = new EventEmitter<DashboardWidgetPlacement[]>();

  widgets: DashboardWidgetPlacement[] = [];
  editMode = false;
  pickerOpen = false;
  widgetSearch = '';
  loadingLayout = false;
  savingLayout = false;
  layoutMessage = '';
  readonly unit = 90;
  pendingCell: EmptyDashboardCell = { x: 0, y: 0 };

  private readonly saveRequests = new Subject<void>();
  private readonly subscriptions = new Subscription();
  private inputCache = new WeakMap<DashboardWidgetPlacement, Record<string, unknown>>();

  constructor(
    private readonly dashboardLayout: DashboardLayout,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.saveRequests.pipe(debounceTime(400)).subscribe(() => this.save()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.inputCache = new WeakMap();
    if (changes['dashboardId'] || changes['appId'] || changes['ownerScope'] || changes['defaultWidgets'] || changes['defaultDashboard'] || changes['dashboardContext']) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get availableDefinitions(): readonly DashboardWidgetDefinition[] {
    return this.definitions.filter((definition) => definition.apps.includes(this.defaultDashboard?.appId ?? this.appId));
  }

  get filteredDefinitions(): readonly DashboardWidgetDefinition[] {
    const search = this.widgetSearch.trim().toLocaleLowerCase();
    if (!search) return this.availableDefinitions;
    return this.availableDefinitions.filter((definition) =>
      `${definition.name} ${definition.description} ${definition.template}`.toLocaleLowerCase().includes(search),
    );
  }

  get gridRows(): number {
    return Math.max(this.editMode ? 12 : 1, ...this.widgets.map((item) => item.y + item.height));
  }

  get gridMinHeight(): string {
    return `${this.gridRows * this.unit + Math.max(0, this.gridRows - 1) * this.gap}px`;
  }

  get emptyCells(): EmptyDashboardCell[] {
    if (!this.editMode) return [];
    const occupied = new Set<string>();
    this.widgets.forEach((item) => {
      for (let y = item.y; y < item.y + item.height; y += 1) {
        for (let x = item.x; x < item.x + item.width; x += 1) occupied.add(`${x}:${y}`);
      }
    });
    const cells: EmptyDashboardCell[] = [];
    for (let y = 0; y < this.gridRows; y += 1) {
      for (let x = 0; x < this.columns; x += 1) {
        if (!occupied.has(`${x}:${y}`)) cells.push({ x, y });
      }
    }
    return cells;
  }

  definitionFor(placement: DashboardWidgetPlacement): DashboardWidgetDefinition {
    return this.definitions.find((definition) => definition.widgetId === placement.widgetId)
      ?? WIDGET_DEFINITIONS[0];
  }

  componentFor(placement: DashboardWidgetPlacement): Type<unknown> {
    switch (this.definitionFor(placement).template) {
      case 'welcome': return DashboardWelcomeWidget;
      case 'number': return DashboardNumberWidget;
      case 'record-metric': return DashboardRecordMetric;
      case 'percentage': return DashboardPercentageWidget;
      case 'graph': return DashboardGraphWidget;
      case 'sankey': return DashboardSankeyWidget;
      case 'report': return ReportWidget;
      case 'list': return DashboardListWidget;
      case 'position': return DashboardPositionWidget;
      case 'finance-money': return DashboardFinanceMoneyWidget;
    }
  }

  inputsFor(placement: DashboardWidgetPlacement): Record<string, unknown> {
    const cached = this.inputCache.get(placement);
    if (cached) return cached;
    const definition = this.definitionFor(placement);
    const inputs: Record<string, unknown> = {
      ...definition.inputs,
      ...placement.inputs,
      ...this.lockedInputs,
      ...this.lockedWidgetInputs[placement.widgetId],
      layout: { xs: { width: placement.width, height: placement.height } },
    };
    if (this.defaultDashboard?.record) {
      const ownedInputs = resolveDashboardPlacements(this.defaultDashboard, this.dashboardContext, this.definitions)
        .find(item => item.widgetId === placement.widgetId)?.inputs;
      Object.assign(inputs, ownedInputs);
    }
    if (this.queryScope) {
      const scope = this.queryScope;
      const scoped = (query: unknown): unknown => {
        if (!query || typeof query !== 'object') return query;
        const value = query as Record<string, unknown>;
        return { ...value, source: scope.source, where: { type: 'group', operator: 'and', children: [
          ...(value['where'] ? [value['where']] : []), scope.where,
        ] } };
      };
      for (const key of ['query', 'valueQuery']) if (inputs[key]) inputs[key] = scoped(inputs[key]);
      if (Array.isArray(inputs['graphQueries'])) inputs['graphQueries'] = inputs['graphQueries'].map(series => ({ ...series, query: scoped(series.query) }));
    }
    // Keep query identities stable across change detection, avoiding repeated HTTP loads.
    this.inputCache.set(placement, inputs);
    return inputs;
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (!this.editMode) this.pickerOpen = false;
  }

  openPicker(cell: EmptyDashboardCell): void {
    this.pendingCell = cell;
    this.widgetSearch = '';
    this.pickerOpen = true;
  }

  closePicker(): void {
    this.pickerOpen = false;
    this.widgetSearch = '';
  }

  addWidget(definition: DashboardWidgetDefinition): void {
    const placement: DashboardWidgetPlacement = {
      id: globalThis.crypto?.randomUUID?.() ?? `${definition.widgetId}.${Date.now()}`,
      widgetId: definition.widgetId,
      x: this.pendingCell.x,
      y: this.pendingCell.y,
      width: definition.limits.defaultWidth,
      height: definition.limits.defaultHeight,
    };
    this.applyWidgets([placement, ...this.widgets]);
    this.pickerOpen = false;
  }

  updateWidget(updated: DashboardWidgetPlacement): void {
    const remaining = this.widgets.filter((item) => item.id !== updated.id);
    this.applyWidgets([updated, ...remaining]);
  }

  removeWidget(id: string): void {
    this.applyWidgets(this.widgets.filter((item) => item.id !== id));
  }

  resetToDefaults(): void {
    this.applyWidgets(this.defaults());
  }

  private get effectiveDashboardId(): string {
    return this.dashboardId || (this.defaultDashboard ? resolveDashboardId(this.defaultDashboard, this.dashboardContext) : '');
  }

  private load(): void {
    const dashboardId = this.effectiveDashboardId;
    if (!dashboardId) return;
    this.loadingLayout = true;
    this.layoutMessage = '';
    this.subscriptions.add(this.dashboardLayout.get(dashboardId, this.ownerScope).subscribe({
      next: (document) => {
        this.deferLayoutUpdate(() => {
          const placements = document.source === 'default' ? this.defaults() : document.widgets;
          this.widgets = this.normalize(placements);
          this.loadingLayout = false;
          this.widgetsChange.emit(this.widgets);
        });
      },
      error: () => {
        this.deferLayoutUpdate(() => {
          this.widgets = this.normalize(this.defaults());
          this.loadingLayout = false;
          this.layoutMessage = 'Using the default dashboard layout until saved layouts are available.';
          this.widgetsChange.emit(this.widgets);
        });
      },
    }));
  }

  private deferLayoutUpdate(update: () => void): void {
    queueMicrotask(() => {
      update();
      this.cdr.detectChanges();
    });
  }

  private defaults(): DashboardWidgetPlacement[] {
    const widgets = this.defaultWidgets ?? (this.defaultDashboard
      ? resolveDashboardPlacements(this.defaultDashboard, this.dashboardContext, this.definitions)
      : defaultDashboardPlacements(this.appId));
    return widgets.map(item => ({ ...item }));
  }

  private applyWidgets(widgets: DashboardWidgetPlacement[]): void {
    this.widgets = this.normalize(widgets);
    this.widgetsChange.emit(this.widgets);
    this.saveRequests.next();
  }

  private save(): void {
    const dashboardId = this.effectiveDashboardId;
    if (!dashboardId) return;
    this.savingLayout = true;
    this.layoutMessage = '';
    this.subscriptions.add(this.dashboardLayout.save(dashboardId, {
      ownerScope: this.ownerScope,
      widgets: this.widgets,
    }).subscribe({
      next: (document) => {
        this.widgets = this.normalize(document.widgets);
        this.savingLayout = false;
        this.layoutMessage = 'Dashboard layout saved.';
      },
      error: () => {
        this.savingLayout = false;
        this.layoutMessage = 'Dashboard layout could not be saved.';
      },
    }));
  }

  private normalize(widgets: DashboardWidgetPlacement[]): DashboardWidgetPlacement[] {
    const supported = new Set(this.definitions.map(definition => definition.widgetId));
    return normalizeDashboardPlacements(widgets.filter(widget => supported.has(widget.widgetId)), this.definitions, this.columns);
  }
}
